import React, { useEffect } from 'react';
import * as mobxReact from 'mobx-react';
import { UploadImageS3Presentation } from '../../../../../shared/upload-image/upload-image-to-s3.presentation';
import { UploadImageS3WithAIViewModel } from './upload-image-to-s3-ai.viewmodel';
import { useIntl } from 'react-intl';
import { CommonModal } from '../../../../../../../global/modal/common/common-modal';
import {
    GENERATE_PASSPORT_BACKGROUND_BUTTON_TEXT,
    GENERATE_PASSPORT_BACKGROUND_LOADING_INFO_TEXT,
    GENERATE_PASSPORT_BACKGROUND_LOADING_TITLE_TEXT,
    GENERATE_PASSPORT_BACKGROUND_WITH_AI_TEXT,
    TRY_AGAIN_TEXT,
    USE_IMAGE_BUTTON_TEXT
} from './upload-image-to-s3-ai.texts';
import DefaultButton from '../../../../../../../global/default-button/default-button';
import { DefaultCloseModalButton } from '../../../../../../../global/modal/common/common-close-modal-buttons';
import { CONTINUE_BUTTON_TEXT } from '../../../scheduled-emails-form/scheduled-emails-form.texts';
import { ModalLoadingScreen } from '../../../../../shared/modal-loading-screen/modal-loading-screen';
import './upload-image-to-s3-ai.scss';
import classNames from 'classnames';
import { PromptInputScreen } from './prompt-input-screen';
import { GenerateWithAIIcon } from '../../../../../../../global/icons/icons';

interface IProps {
    vm: UploadImageS3WithAIViewModel;
    fieldName: string;
}

const PromptResultScreen = ({ src }): JSX.Element => {
    return (
        <div className='generate-passport-background-modal__image'>
            <img src={src} />
        </div>
    );
};

const GeneratePassportBackgroundImageWithAI = mobxReact.observer((props: IProps): JSX.Element => {
    const intl = useIntl();
    const { vm } = props;
    const isResultScreen = !vm.getIsLoading() && vm.getImgUrl();

    useEffect(() => {
        vm.subscribe();

        return () => {
            vm?.unsubscribe();
        };
    }, []);

    const defaultFooter = (
        <div className='buttons-block buttons-block--from-right'>
            <DefaultButton
                text={intl.formatMessage(CONTINUE_BUTTON_TEXT)}
                modifier={['fat', 'confirm-safe']}
                onClick={vm.sendMessageToAI}
                disabled={!vm.getPrompt().trim() || !!vm.getTooLongPromptError() || vm.getIsLoading()}
            />
            <DefaultCloseModalButton />
        </div>
    );

    const resultScreenFooter = (
        <div className='buttons-block-wrapper'>
            <DefaultButton
                text={intl.formatMessage(TRY_AGAIN_TEXT)}
                modifier={['fat', 'confirm-safe']}
                onClick={vm.tryAgain}
            />
            <div className='buttons-block buttons-block--from-right'>
                <DefaultButton
                    text={intl.formatMessage(USE_IMAGE_BUTTON_TEXT)}
                    modifier={['fat', 'confirm-safe']}
                    onClick={vm.useImage}
                    disabled={!vm.getImgUrl() || vm.getIsLoading()}
                />
                <DefaultCloseModalButton />
            </div>
        </div>
    );

    const showProperModalScreen = (): JSX.Element => {
        if (isResultScreen) {
            return <PromptResultScreen src={vm.getImgUrl()} />;
        } else if (vm.getIsLoading()) {
            return <ModalLoadingScreen
                loadingTitle={GENERATE_PASSPORT_BACKGROUND_LOADING_TITLE_TEXT}
                loadingInfo={GENERATE_PASSPORT_BACKGROUND_LOADING_INFO_TEXT}
            />;
        } else {
            return <PromptInputScreen vm={vm} />;
        }
    };

    return (
        <>
            <div className={classNames('generate-passport-background-wrapper', {
                'image-uploaded': !!vm.image?.assetUrl || !!vm.image?.objectKey
            })}>
                <UploadImageS3Presentation
                    vm={vm}
                    fieldName={props.fieldName}
                />
                {!vm.isImageUploading && <button
                    className='generate-passport-background-button'
                    onClick={vm.openPromptModal}
                >
                    <GenerateWithAIIcon /> {intl.formatMessage(GENERATE_PASSPORT_BACKGROUND_BUTTON_TEXT)}
                </button>}
            </div >

            <CommonModal
                isOpen={vm.getIsPromptModalOpen()}
                title={intl.formatMessage(GENERATE_PASSPORT_BACKGROUND_WITH_AI_TEXT)}
                onRequestClose={vm.closePromptModal}
                className={classNames('generate-passport-background-modal', {
                    'generation-result': isResultScreen
                })}
                footer={isResultScreen ? resultScreenFooter : defaultFooter}
            >
                {showProperModalScreen()}
            </CommonModal>
        </>
    );

});

export { GeneratePassportBackgroundImageWithAI };
